import { createPortal } from 'react-dom';
import type { AiSuggestionHistory } from '../../../api/inquiryChat';
import { commonStrings, messageStrings } from '../../../constants/strings';
import { Icon } from '../../Icon';
import { AiAnswerCopyButton } from '../AiAnswerCopyButton';

type Props = {
  history: AiSuggestionHistory[];
  loading?: boolean;
  onClose: () => void;
};

export function AiHistoryModal({ history, loading = false, onClose }: Props) {
  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex min-h-[100dvh] w-screen items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="mx-4 flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-neutral-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Icon
              icon="aiStar"
              width={20}
              height={20}
              className="text-blue-500"
            />
            <span className="text-base font-bold text-neutral-800">
              {messageStrings.aiHistoryTitle}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={commonStrings.close}
            className="rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-50 hover:text-neutral-600"
          >
            <Icon icon="close" width={20} height={20} />
          </button>
        </div>

        <div
          className="flex-1 space-y-3 overflow-y-auto px-6 py-4"
          style={{ scrollbarWidth: 'thin' }}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12">
              <div className="flex gap-1.5">
                <div className="h-2 w-2 animate-bounce rounded-full bg-blue-400 [animation-delay:-0.3s]" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-blue-400 [animation-delay:-0.15s]" />
                <div className="h-2 w-2 animate-bounce rounded-full bg-blue-400" />
              </div>
              <span className="text-sm font-medium text-blue-500">
                {messageStrings.aiHistoryLoading}
              </span>
            </div>
          ) : history.length === 0 ? (
            <div className="py-8 text-center text-sm text-neutral-400">
              {messageStrings.aiNoHistory}
            </div>
          ) : (
            history.map((item) => (
              <div
                key={item.id}
                className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-3"
              >
                <div className="mb-2 text-xs text-neutral-400">
                  {new Date(item.createdDatetime).toLocaleString('ja-JP')}
                </div>
                <div className="mb-1.5 line-clamp-1 text-xs text-neutral-500">
                  {messageStrings.aiTargetMessage}: {item.originalMessage}
                </div>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">
                    {item.aiAnswer}
                  </div>
                  <AiAnswerCopyButton text={item.aiAnswer} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
